import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API, getAuthHeaders } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Users, Building2, ShoppingCart, TrendingUp,
  Settings, Trash2, Eye, EyeOff, UserPlus, Package, LogOut,
  ChevronDown, ChevronUp, BarChart3, Edit, KeyRound, ClipboardCopy, Copy,
  Calendar, Filter, Layers, Bell, MapPin, Lock, History, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import NotificationBell from '../components/NotificationBell';
import AddressAutocomplete from '../components/AddressAutocomplete';
import LanguageSwitcher from '../components/LanguageSwitcher';

function B2BNetworks({ user, onLogout }) {
  const { i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';

  const [networks, setNetworks] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('analytics');
  const [expandedNetwork, setExpandedNetwork] = useState(null);
  const [companyCode, setCompanyCode] = useState(user?.company_code || '');
  const [editingCode, setEditingCode] = useState(false);
  const [codeInput, setCodeInput] = useState('');

  // Dialogs
  const [showNetworkDialog, setShowNetworkDialog] = useState(false);
  const [showManagerDialog, setShowManagerDialog] = useState(false);
  const [showLegalDialog, setShowLegalDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showEditManagerDialog, setShowEditManagerDialog] = useState(false);
  const [editingNetwork, setEditingNetwork] = useState(null);
  const [selectedNetworkId, setSelectedNetworkId] = useState(null);
  const [selectedManager, setSelectedManager] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  // Network orders
  const [networkOrders, setNetworkOrders] = useState({});
  const [loadingOrders, setLoadingOrders] = useState(null);

  // Analytics filters
  const [analyticsPeriod, setAnalyticsPeriod] = useState('week');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [analyticsTab, setAnalyticsTab] = useState('products');

  const [showAddAddressDialog, setShowAddAddressDialog] = useState(false);
  const [addressForm, setAddressForm] = useState({ label: '', address_line: '', city: 'Алматы', phone: '', is_delivery: true });

  // Notification settings
  const [notifyNetworkOrders, setNotifyNetworkOrders] = useState(true);

  // Full settings state
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPasswordField, setShowNewPasswordField] = useState(false);
  const [loginHistory, setLoginHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [settingsNotifications, setSettingsNotifications] = useState({ order_updates: true, promotions: false, newsletter: false });
  const [savingNotifications, setSavingNotifications] = useState(false);

  // Owner company (no network mode)
  const [companyData, setCompanyData] = useState({ legal_entities: [], addresses: [] });

  // Forms
  const [networkForm, setNetworkForm] = useState({ name: '', description: '', settings: { can_edit_legal: false, can_add_legal: false, can_add_addresses: false } });
  const [managerForm, setManagerForm] = useState({ username: '', email: '', password: '', full_name: '', phone: '' });
  const [editManagerForm, setEditManagerForm] = useState({ full_name: '', phone: '', email: '' });
  const [legalForm, setLegalForm] = useState({ company_name: '', bin_iin: '', legal_address: '', contact_person: '', phone: '', bank_name: '', bank_account: '', is_default: false });

  const isOwner = user?.role === 'B2B' && !user?.parent_user_id;
  
  // Admin impersonation
  const isAdminViewingAsClient = typeof window !== 'undefined' && localStorage.getItem('admin_token_backup') !== null;
  
  const handleReturnToAdmin = () => {
    const adminToken = localStorage.getItem('admin_token_backup');
    if (adminToken) {
      localStorage.setItem('token', adminToken);
      localStorage.removeItem('admin_token_backup');
      localStorage.removeItem('admin_email_backup');
      toast.success('Вы вернулись в админку');
      window.location.href = `/${currentLang}/admin/clients`;
    }
  };

  useEffect(() => { fetchData(); fetchCompanyCode(); fetchNotificationSettings(); fetchCompanyData(); fetchLoginHistory(); fetchUserNotifications(); }, []);

  const fetchLoginHistory = async () => {
    setLoadingHistory(true);
    try {
      const res = await axios.get(`${API}/user/login-history`, { headers: getAuthHeaders() });
      setLoginHistory(res.data);
    } catch {} finally { setLoadingHistory(false); }
  };

  const fetchUserNotifications = async () => {
    if (user?.notification_preferences) {
      setSettingsNotifications(user.notification_preferences);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) { toast.error('Пароли не совпадают'); return; }
    if (passwordData.new_password.length < 6) { toast.error('Пароль должен быть не менее 6 символов'); return; }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/user/password`, { current_password: passwordData.current_password, new_password: passwordData.new_password }, { headers: getAuthHeaders() });
      toast.success('Пароль успешно изменён');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) { toast.error(error.response?.data?.detail || 'Ошибка смены пароля'); }
    finally { setChangingPassword(false); }
  };

  const handleSaveNotifications = async () => {
    setSavingNotifications(true);
    try {
      await axios.put(`${API}/user/notifications`, settingsNotifications, { headers: getAuthHeaders() });
      toast.success('Настройки уведомлений сохранены');
    } catch { toast.error('Ошибка сохранения'); }
    finally { setSavingNotifications(false); }
  };

  const fetchNotificationSettings = async () => {
    if (!isOwner) return;
    try {
      const res = await axios.get(`${API}/b2b/notification-settings`, { headers: getAuthHeaders() });
      setNotifyNetworkOrders(res.data.notify_network_orders ?? true);
    } catch {}
  };

  const fetchCompanyData = async () => {
    if (!isOwner) return;
    try {
      const res = await axios.get(`${API}/b2b/company`, { headers: getAuthHeaders() });
      setCompanyData(res.data);
    } catch {}
  };

  const [editingLegalId, setEditingLegalId] = useState(null);

  const handleDeleteCompanyLegal = async (entityId) => {
    if (!window.confirm('Удалить юридическое лицо?')) return;
    try {
      await axios.delete(`${API}/b2b/legal-entities/${entityId}`, { headers: getAuthHeaders() });
      toast.success('Юрлицо удалено');
      fetchCompanyData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка удаления'); }
  };

  const handleDeleteNetworkLegal = async (networkId, entityId) => {
    if (!window.confirm('Удалить юридическое лицо?')) return;
    try {
      await axios.delete(`${API}/b2b/networks/${networkId}/legal-entities/${entityId}`, { headers: getAuthHeaders() });
      toast.success('Юрлицо удалено');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка удаления'); }
  };

  const handleEditCompanyLegal = (entity) => {
    setEditingLegalId(entity.id);
    setSelectedNetworkId(null);
    setLegalForm({
      company_name: entity.company_name || '',
      bin_iin: entity.bin_iin || '',
      legal_address: entity.legal_address || '',
      contact_person: entity.contact_person || '',
      phone: entity.phone || '',
      bank_name: entity.bank_name || '',
      bank_account: entity.bank_account || '',
      is_default: entity.is_default || false,
    });
    setShowLegalDialog(true);
  };

  const handleAddCompanyAddress = async (e) => {
    e.preventDefault();
    if (!addressForm.address_line) return;
    try {
      await axios.post(`${API}/addresses`, addressForm, { headers: getAuthHeaders() });
      toast.success('Адрес добавлен');
      setShowAddAddressDialog(false);
      setAddressForm({ label: '', address_line: '', city: 'Алматы', phone: '', is_delivery: true });
      fetchCompanyData();
    } catch { toast.error('Ошибка'); }
  };

  const toggleNotifyNetworkOrders = async (value) => {
    try {
      await axios.put(`${API}/b2b/notification-settings`, { notify_network_orders: value }, { headers: getAuthHeaders() });
      setNotifyNetworkOrders(value);
      toast.success(value ? 'Уведомления от сетей включены' : 'Уведомления от сетей отключены');
    } catch { toast.error('Ошибка сохранения настроек'); }
  };

  const handleAddNetworkAddress = async (e) => {
    e.preventDefault();
    if (!addressForm.address_line) return;
    try {
      const payload = { ...addressForm, is_default: true };
      if (selectedNetworkId) {
        payload.network_id = selectedNetworkId;
      }
      await axios.post(`${API}/addresses`, payload, { headers: getAuthHeaders() });
      toast.success('Адрес добавлен');
      setShowAddAddressDialog(false);
      setAddressForm({ label: '', address_line: '', city: 'Алматы', phone: '', is_delivery: true });
      fetchData();
      fetchCompanyData();
    } catch { toast.error('Ошибка добавления адреса'); }
  };

  const handleSetDeliveryAddress = async (addressId) => {
    try {
      await axios.put(`${API}/addresses/${addressId}/set-delivery`, {}, { headers: getAuthHeaders() });
      toast.success('Адрес доставки установлен');
      fetchData();
    } catch { toast.error('Ошибка'); }
  };

  const handleSetAddressNetwork = async (addressId, networkId) => {
    try {
      await axios.put(
        `${API}/addresses/${addressId}/network`,
        { network_id: networkId || null },
        { headers: getAuthHeaders() }
      );
      toast.success(networkId ? 'Адрес привязан к сети' : 'Адрес отвязан от сети');
      fetchData();
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm('Удалить этот адрес?')) return;
    try {
      await axios.delete(`${API}/addresses/${addressId}`, { headers: getAuthHeaders() });
      toast.success('Адрес удалён');
      fetchData();
    } catch { toast.error('Ошибка удаления'); }
  };

  const fetchAnalytics = useCallback(async (period, from, to) => {
    if (!isOwner) return;
    try {
      let params = `?period=${period}`;
      if (from) params += `&date_from=${from}`;
      if (to) params += `&date_to=${to}`;
      const res = await axios.get(`${API}/b2b/networks/analytics${params}`, { headers: getAuthHeaders() });
      if (res.data) setAnalytics(res.data);
    } catch {}
  }, [isOwner]);

  useEffect(() => {
    if (isOwner) fetchAnalytics(analyticsPeriod, dateFrom, dateTo);
  }, [analyticsPeriod, dateFrom, dateTo, isOwner, fetchAnalytics]);

  const fetchCompanyCode = async () => {
    try {
      const res = await axios.get(`${API}/b2b/company-code`, { headers: getAuthHeaders() });
      setCompanyCode(res.data.company_code || '');
    } catch {}
  };

  const saveCompanyCode = async () => {
    if (codeInput.length < 3) { toast.error('Минимум 3 символа'); return; }
    try {
      const res = await axios.put(`${API}/b2b/company-code`, { company_code: codeInput }, { headers: getAuthHeaders() });
      setCompanyCode(res.data.company_code);
      setEditingCode(false);
      toast.success('Код компании сохранён');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const netRes = await axios.get(`${API}/b2b/networks`, { headers: getAuthHeaders() });
      setNetworks(netRes.data);
    } catch (e) {
      if (e.response?.status !== 403) toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const fetchNetworkOrders = async (networkId) => {
    if (networkOrders[networkId]) return;
    setLoadingOrders(networkId);
    try {
      const res = await axios.get(`${API}/b2b/networks/${networkId}/orders?limit=10`, { headers: getAuthHeaders() });
      setNetworkOrders(prev => ({ ...prev, [networkId]: res.data }));
    } catch { toast.error('Ошибка загрузки заказов'); }
    finally { setLoadingOrders(null); }
  };

  // Network CRUD
  const handleSaveNetwork = async () => {
    try {
      if (editingNetwork) {
        await axios.put(`${API}/b2b/networks/${editingNetwork.id}`, networkForm, { headers: getAuthHeaders() });
        toast.success('Сеть обновлена');
      } else {
        await axios.post(`${API}/b2b/networks`, networkForm, { headers: getAuthHeaders() });
        toast.success('Сеть создана');
      }
      setShowNetworkDialog(false);
      setEditingNetwork(null);
      setNetworkForm({ name: '', description: '', settings: { can_edit_legal: false, can_add_legal: false, can_add_addresses: false } });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка');
    }
  };

  const handleDeleteNetwork = async (id) => {
    if (!window.confirm('Удалить сеть? Менеджеры будут деактивированы.')) return;
    try {
      await axios.delete(`${API}/b2b/networks/${id}`, { headers: getAuthHeaders() });
      toast.success('Сеть удалена');
      fetchData();
    } catch { toast.error('Ошибка удаления'); }
  };

  const openEditNetwork = (net) => {
    setEditingNetwork(net);
    setNetworkForm({ name: net.name, description: net.description || '', settings: net.settings || {} });
    setShowNetworkDialog(true);
  };

  // Manager CRUD
  const handleCreateManager = async () => {
    if (!managerForm.username || !managerForm.password || !managerForm.full_name) {
      toast.error('Заполните: логин, пароль и ФИО');
      return;
    }
    if (managerForm.password.length < 6) {
      toast.error('Пароль должен быть не менее 6 символов');
      return;
    }
    try {
      const payload = {
        username: managerForm.username,
        password: managerForm.password,
        full_name: managerForm.full_name,
        phone: managerForm.phone || null,
        email: managerForm.email || null,
      };
      await axios.post(`${API}/b2b/networks/${selectedNetworkId}/managers`, payload, { headers: getAuthHeaders() });
      toast.success(`Менеджер создан! Логин: ${managerForm.username}`);
      setShowManagerDialog(false);
      setManagerForm({ username: '', email: '', password: '', full_name: '', phone: '' });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка');
    }
  };

  const handleRemoveManager = async (networkId, managerId) => {
    if (!window.confirm('Удалить менеджера?')) return;
    try {
      await axios.delete(`${API}/b2b/networks/${networkId}/managers/${managerId}`, { headers: getAuthHeaders() });
      toast.success('Менеджер удалён');
      fetchData();
    } catch { toast.error('Ошибка удаления'); }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Пароль должен быть не менее 6 символов');
      return;
    }
    try {
      await axios.put(
        `${API}/b2b/networks/${selectedNetworkId}/managers/${selectedManager.id}/password`,
        { new_password: newPassword },
        { headers: getAuthHeaders() }
      );
      toast.success(`Пароль для ${selectedManager.full_name} обновлён`);
      setShowPasswordDialog(false);
      setNewPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка');
    }
  };

  const handleEditManager = async () => {
    try {
      await axios.put(
        `${API}/b2b/networks/${selectedNetworkId}/managers/${selectedManager.id}`,
        editManagerForm,
        { headers: getAuthHeaders() }
      );
      toast.success('Данные менеджера обновлены');
      setShowEditManagerDialog(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка');
    }
  };

  const copyCredentials = (manager) => {
    const text = `Логин: ${manager.username}\nДанные для входа на ainanan.kz`;
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Данные скопированы в буфер обмена');
    }).catch(() => {
      toast.error('Не удалось скопировать');
    });
  };

  // Legal Entity
  const handleSaveLegal = async () => {
    if (!legalForm.company_name) { toast.error('Введите название компании'); return; }
    
    if (!selectedNetworkId) {
      // Direct company mode (no network)
      try {
        if (editingLegalId) {
          await axios.put(`${API}/b2b/legal-entities/${editingLegalId}`, legalForm, { headers: getAuthHeaders() });
          toast.success('Юрлицо обновлено');
        } else {
          await axios.post(`${API}/b2b/legal-entities`, legalForm, { headers: getAuthHeaders() });
          toast.success('Юрлицо добавлено');
        }
        setShowLegalDialog(false);
        setEditingLegalId(null);
        setLegalForm({ company_name: '', bin_iin: '', legal_address: '', contact_person: '', phone: '', bank_name: '', bank_account: '', is_default: false });
        fetchCompanyData();
      } catch (e) { toast.error(e.response?.data?.detail || 'Ошибка'); }
      return;
    }
    try {
      if (editingLegalId) {
        await axios.put(`${API}/b2b/networks/${selectedNetworkId}/legal-entities/${editingLegalId}`, legalForm, { headers: getAuthHeaders() });
        toast.success('Юрлицо обновлено');
      } else {
        await axios.post(`${API}/b2b/networks/${selectedNetworkId}/legal-entities`, legalForm, { headers: getAuthHeaders() });
        toast.success('Юрлицо добавлено');
      }
      setShowLegalDialog(false);
      setEditingLegalId(null);
      setLegalForm({ company_name: '', bin_iin: '', legal_address: '', contact_person: '', phone: '', bank_name: '', bank_account: '', is_default: false });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Ошибка');
    }
  };

  const toggleExpand = (id) => {
    if (expandedNetwork === id) {
      setExpandedNetwork(null);
    } else {
      setExpandedNetwork(id);
      fetchNetworkOrders(id);
    }
  };

  const fmt = (n) => Number(n || 0).toLocaleString('ru-RU');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="b2b-networks-page">
      {/* Admin impersonation banner */}
      {isAdminViewingAsClient && (
        <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm" data-testid="admin-impersonation-banner">
          <span>Вы просматриваете как клиент: <strong>{user?.company_name || user?.email}</strong></span>
          <Button size="sm" variant="secondary" onClick={handleReturnToAdmin} className="h-7 text-xs" data-testid="return-to-admin-btn">
            <ArrowLeft className="w-3 h-3 mr-1" />
            Вернуться в админку
          </Button>
        </div>
      )}
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/${currentLang}`}>
                <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Назад</Button>
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Мои сети</h1>
                <p className="text-sm text-gray-500">
                  {isOwner ? 'Управление сетями и менеджерами' : `Сеть: ${networks[0]?.name || '—'}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationBell user={user} />
              <LanguageSwitcher />
              <Button variant="ghost" size="sm" onClick={onLogout}>
                <LogOut className="w-4 h-4 mr-1" />Выйти
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 py-6">
        {isOwner ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="analytics" className="flex items-center gap-2" data-testid="tab-analytics">
                <BarChart3 className="w-4 h-4" />Аналитика
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="tab-settings">
                <Settings className="w-4 h-4" />Настройки
              </TabsTrigger>
            </TabsList>

            {/* Networks - always visible */}
              <div className="space-y-6 mb-6">
                {/* Company Code */}
                {isOwner && (
                  <Card className="border-emerald-200 bg-emerald-50/50">
                    <CardContent className="pt-5 pb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Код компании для менеджеров</p>
                          <p className="text-xs text-gray-500">Менеджеры вводят этот код при входе: логин + пароль + код компании</p>
                        </div>
                        {editingCode ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={codeInput}
                              onChange={e => setCodeInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                              placeholder="alma"
                              className="w-40 h-8 text-sm"
                              data-testid="company-code-input"
                            />
                            <Button size="sm" className="h-8 bg-emerald-700 hover:bg-emerald-800" onClick={saveCompanyCode} data-testid="save-code-btn">Сохранить</Button>
                            <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingCode(false)}>Отмена</Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {companyCode ? (
                              <>
                                <code className="px-3 py-1 bg-white border rounded font-mono text-sm font-bold text-emerald-700" data-testid="company-code-display">{companyCode}</code>
                                <Button size="sm" variant="ghost" className="h-8" onClick={() => { navigator.clipboard.writeText(companyCode); toast.success('Код скопирован'); }} title="Копировать">
                                  <ClipboardCopy className="w-3.5 h-3.5" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-8" onClick={() => { setCodeInput(companyCode); setEditingCode(true); }}>
                                  <Edit className="w-3.5 h-3.5" />
                                </Button>
                              </>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => { setCodeInput(''); setEditingCode(true); }} data-testid="set-code-btn">
                                Задать код
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* KPI */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Building2 className="w-5 h-5 text-blue-600" /></div><div><p className="text-sm text-gray-500">Сетей</p><p className="text-2xl font-bold">{networks.length}</p></div></div></CardContent></Card>
                  <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><Users className="w-5 h-5 text-emerald-600" /></div><div><p className="text-sm text-gray-500">Менеджеров</p><p className="text-2xl font-bold">{networks.reduce((a, n) => a + (n.managers?.length || 0), 0)}</p></div></div></CardContent></Card>
                  <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><ShoppingCart className="w-5 h-5 text-purple-600" /></div><div><p className="text-sm text-gray-500">Заказов</p><p className="text-2xl font-bold">{networks.reduce((a, n) => a + (n.stats?.orders_count || 0), 0)}</p></div></div></CardContent></Card>
                  <Card><CardContent className="pt-6"><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-orange-600" /></div><div><p className="text-sm text-gray-500">Сумма заказов</p><p className="text-2xl font-bold">{fmt(networks.reduce((a, n) => a + (n.stats?.total_revenue || 0), 0))} ₸</p></div></div></CardContent></Card>
                </div>

                {/* Add Network Button */}
                <div className="flex justify-end">
                  <Button onClick={() => { setEditingNetwork(null); setNetworkForm({ name: '', description: '', settings: { can_edit_legal: false, can_add_legal: false, can_add_addresses: false } }); setShowNetworkDialog(true); }} className="bg-emerald-700 hover:bg-emerald-800" data-testid="add-network-btn">
                    <Plus className="w-4 h-4 mr-2" />Добавить сеть
                  </Button>
                </div>

                {/* Networks List */}
                {networks.length === 0 ? (
                  <div className="space-y-6">
                    {/* Company direct mode */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2"><Building2 className="w-5 h-5 text-emerald-600" />{user?.company_name || 'Ваша компания'}</CardTitle>
                        <CardDescription>Управляйте юрлицами и адресами вашей компании</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Legal Entities */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-sm">Юридические лица</h3>
                            <Button size="sm" variant="outline" onClick={() => { setSelectedNetworkId(null); setEditingLegalId(null); setLegalForm({ company_name: '', bin_iin: '', legal_address: '', contact_person: '', phone: '', bank_name: '', bank_account: '', is_default: false }); setShowLegalDialog(true); }} data-testid="add-company-legal-btn">
                              <Plus className="w-3 h-3 mr-1" />Добавить
                            </Button>
                          </div>
                          {companyData.legal_entities.length === 0 ? (
                            <p className="text-sm text-gray-400 py-3 text-center">Добавьте юрлицо для оформления заказов</p>
                          ) : (
                            <div className="space-y-2">
                              {companyData.legal_entities.map(e => (
                                <div key={e.id} className="p-3 bg-gray-50 rounded-lg flex items-start justify-between">
                                  <div>
                                    <p className="text-sm font-medium">{e.company_name}</p>
                                    <p className="text-xs text-gray-500">БИН: {e.bin_iin || '—'}{e.legal_address ? ` | ${e.legal_address}` : ''}</p>
                                    {e.is_default && <Badge className="mt-1 bg-emerald-100 text-emerald-700 text-xs">По умолчанию</Badge>}
                                  </div>
                                  <div className="flex gap-1 ml-2 shrink-0">
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => handleEditCompanyLegal(e)} data-testid={`edit-legal-${e.id}`}>
                                      <Edit className="w-3.5 h-3.5 text-gray-500" />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteCompanyLegal(e.id)} data-testid={`delete-legal-${e.id}`}>
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Addresses */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-sm flex items-center gap-2"><MapPin className="w-4 h-4" />Адреса</h3>
                            <Button size="sm" variant="outline" onClick={() => { setSelectedNetworkId(null); setAddressForm({ label: '', address_line: '', city: 'Алматы', phone: '', is_delivery: true }); setShowAddAddressDialog(true); }} data-testid="add-company-address-btn">
                              <Plus className="w-3 h-3 mr-1" />Добавить
                            </Button>
                          </div>
                          {companyData.addresses.length === 0 ? (
                            <p className="text-sm text-gray-400 py-3 text-center">Добавьте адрес доставки</p>
                          ) : (
                            <div className="space-y-2">
                              {companyData.addresses.map(addr => (
                                <div key={addr.id} className="p-2 bg-gray-50 rounded-lg flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-medium">{addr.label || 'Адрес'}: {addr.address_line}</p>
                                    <p className="text-xs text-gray-500">{addr.city}{addr.phone ? ` | ${addr.phone}` : ''}</p>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {addr.is_delivery ? (
                                      <Badge className="bg-emerald-100 text-emerald-700 text-xs">Адрес доставки</Badge>
                                    ) : (
                                      <Button size="sm" variant="ghost" className="text-xs text-gray-500 h-7" onClick={() => handleSetDeliveryAddress(addr.id)}>
                                        Сделать адресом доставки
                                      </Button>
                                    )}
                                    <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0" onClick={() => handleDeleteAddress(addr.id)}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>

                    {/* Expand hint */}
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-500 mb-3">Открыли вторую точку? Создайте сеть для управления несколькими локациями</p>
                      <Button variant="outline" onClick={() => setShowNetworkDialog(true)} data-testid="create-first-network-btn">
                        <Plus className="w-4 h-4 mr-2" />Создать сеть
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {networks.map(net => (
                      <Card key={net.id} data-testid={`network-card-${net.id}`}>
                        <CardHeader className="cursor-pointer" onClick={() => toggleExpand(net.id)}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                                <Building2 className="w-5 h-5 text-emerald-600" />
                              </div>
                              <div>
                                <CardTitle className="text-lg">{net.name}</CardTitle>
                                <CardDescription>{net.description || 'Без описания'}</CardDescription>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-6 text-sm text-gray-500">
                                <span><Users className="w-4 h-4 inline mr-1" />{net.managers?.length || 0} менеджеров</span>
                                <span><Package className="w-4 h-4 inline mr-1" />{net.stats?.orders_count || 0} заказов</span>
                              <span className="font-semibold text-gray-900">{fmt(net.stats?.total_revenue || 0)} ₸</span>
                              </div>
                              {expandedNetwork === net.id ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                            </div>
                          </div>
                        </CardHeader>

                        {expandedNetwork === net.id && (
                          <CardContent className="border-t pt-6">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                              {/* Managers */}
                              <div>
                                <div className="flex items-center justify-between mb-3">
                                  <h3 className="font-semibold flex items-center gap-2"><Users className="w-4 h-4" />Менеджеры</h3>
                                  <Button size="sm" variant="outline" onClick={() => { setSelectedNetworkId(net.id); setManagerForm({ email: '', password: '', full_name: '', phone: '' }); setShowManagerDialog(true); }} data-testid={`add-manager-btn-${net.id}`}>
                                    <UserPlus className="w-3 h-3 mr-1" />Добавить
                                  </Button>
                                </div>
                                {(net.managers || []).length === 0 ? (
                                  <p className="text-sm text-gray-400 py-4 text-center">Нет менеджеров</p>
                                ) : (
                                  <div className="space-y-2">
                                    {net.managers.map(m => (
                                      <div key={m.id} className="p-2 bg-gray-50 rounded-lg" data-testid={`manager-card-${m.id}`}>
                                        <div className="flex items-center justify-between mb-1">
                                          <p className="text-sm font-medium">{m.full_name}</p>
                                          <div className="flex gap-1">
                                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-500" title="Скопировать данные"
                                              onClick={() => copyCredentials(m)} data-testid={`copy-creds-${m.id}`}>
                                              <ClipboardCopy className="w-3 h-3" />
                                            </Button>
                                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-500" title="Сбросить пароль"
                                              onClick={() => { setSelectedManager(m); setSelectedNetworkId(net.id); setNewPassword(''); setShowPasswordDialog(true); }}>
                                              <KeyRound className="w-3 h-3" />
                                            </Button>
                                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-500" title="Редактировать"
                                              onClick={() => { setSelectedManager(m); setSelectedNetworkId(net.id); setEditManagerForm({ full_name: m.full_name || '', phone: m.phone || '', email: m.email || '' }); setShowEditManagerDialog(true); }}>
                                              <Edit className="w-3 h-3" />
                                            </Button>
                                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500 hover:text-red-700" title="Удалить"
                                              onClick={() => handleRemoveManager(net.id, m.id)}>
                                              <Trash2 className="w-3 h-3" />
                                            </Button>
                                          </div>
                                        </div>
                                        <p className="text-xs text-gray-500">Логин: <span className="font-mono font-medium text-gray-700">{m.username || '—'}</span></p>
                                        {m.network_ids && m.network_ids.length > 1 && (
                                          <div className="flex flex-wrap gap-1 mt-1">
                                            {m.network_ids.map(nid => {
                                              const netInfo = networks.find(n => n.id === nid);
                                              return netInfo ? (
                                                <span key={nid} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{netInfo.name}</span>
                                              ) : null;
                                            })}
                                          </div>
                                        )}
                                        {m.email && <p className="text-xs text-gray-400">{m.email}</p>}
                                        {m.phone && <p className="text-xs text-gray-400">{m.phone}</p>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Legal Entities */}
                              <div>
                                <div className="flex items-center justify-between mb-3">
                                  <h3 className="font-semibold flex items-center gap-2"><Building2 className="w-4 h-4" />Юрлица</h3>
                                  <Button size="sm" variant="outline" onClick={() => { setSelectedNetworkId(net.id); setLegalForm({ company_name: '', bin_iin: '', legal_address: '', contact_person: '', phone: '', bank_name: '', bank_account: '', is_default: false }); setShowLegalDialog(true); }}>
                                    <Plus className="w-3 h-3 mr-1" />Добавить
                                  </Button>
                                </div>
                                {(net.legal_entities || []).length === 0 ? (
                                  <p className="text-sm text-gray-400 py-4 text-center">Нет юрлиц</p>
                                ) : (
                                  <div className="space-y-2">
                                    {net.legal_entities.map(le => (
                                      <div key={le.id} className="p-2 bg-gray-50 rounded-lg flex items-start justify-between">
                                        <div>
                                          <p className="text-sm font-medium">{le.company_name}</p>
                                          <p className="text-xs text-gray-500">БИН: {le.bin_iin || '—'}</p>
                                          {le.legal_address && <p className="text-xs text-gray-500">{le.legal_address}</p>}
                                          {le.is_default && <Badge className="mt-1 bg-emerald-100 text-emerald-700 text-xs">По умолчанию</Badge>}
                                        </div>
                                        <div className="flex gap-1 ml-2 shrink-0">
                                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => { setEditingLegalId(le.id); setSelectedNetworkId(net.id); setLegalForm({ company_name: le.company_name || '', bin_iin: le.bin_iin || '', legal_address: le.legal_address || '', contact_person: le.contact_person || '', phone: le.phone || '', bank_name: le.bank_name || '', bank_account: le.bank_account || '', is_default: le.is_default || false }); setShowLegalDialog(true); }}>
                                            <Edit className="w-3.5 h-3.5 text-gray-500" />
                                          </Button>
                                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteNetworkLegal(net.id, le.id)}>
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </Button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Addresses */}
                              <div>
                                <div className="flex items-center justify-between mb-3">
                                  <h3 className="font-semibold flex items-center gap-2"><MapPin className="w-4 h-4" />Адреса</h3>
                                  <Button size="sm" variant="outline" onClick={() => { setSelectedNetworkId(net.id); setAddressForm({ label: '', address_line: '', city: 'Алматы', phone: '', is_delivery: true }); setShowAddAddressDialog(true); }}>
                                    <Plus className="w-3 h-3 mr-1" />Добавить
                                  </Button>
                                </div>
                                {(net.addresses || []).length === 0 ? (
                                  <div className="space-y-2">
                                    <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 text-center">⚠️ Нет адресов в этой сети.</p>
                                    {/* Quick-attach existing unassigned address */}
                                    {(() => {
                                      const free = (companyData.addresses || []).filter(a => !a.network_id);
                                      if (free.length === 0) return null;
                                      return (
                                        <div className="text-xs text-gray-600">
                                          Можно прикрепить уже существующий адрес:
                                          <select
                                            className="ml-2 border rounded px-2 py-1 text-xs"
                                            value=""
                                            onChange={(e) => e.target.value && handleSetAddressNetwork(e.target.value, net.id)}
                                            data-testid={`attach-addr-to-${net.id}`}
                                          >
                                            <option value="">— Выбрать адрес —</option>
                                            {free.map(a => (
                                              <option key={a.id} value={a.id}>{a.label || a.address_line}</option>
                                            ))}
                                          </select>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {net.addresses.map(addr => (
                                      <div key={addr.id} className="p-2 bg-gray-50 rounded-lg">
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="min-w-0 flex-1">
                                            <p className="text-sm font-medium truncate">{addr.label || 'Адрес'}: {addr.address_line}</p>
                                            <p className="text-xs text-gray-500">{addr.city}{addr.phone ? ` | ${addr.phone}` : ''}</p>
                                          </div>
                                          <div className="flex items-center gap-2 shrink-0">
                                            {addr.is_delivery ? (
                                              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Адрес доставки</Badge>
                                            ) : (
                                              <Button size="sm" variant="ghost" className="text-xs text-gray-500 h-7" onClick={() => handleSetDeliveryAddress(addr.id)} data-testid={`set-delivery-${addr.id}`}>
                                                Сделать адресом доставки
                                              </Button>
                                            )}
                                            <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0" onClick={() => handleDeleteAddress(addr.id)}>
                                              <Trash2 className="w-3 h-3" />
                                            </Button>
                                          </div>
                                        </div>
                                        {/* Reassign to another network */}
                                        {networks.length > 1 && (
                                          <div className="mt-1.5 flex items-center gap-2 text-xs text-gray-500">
                                            Сеть:
                                            <select
                                              value={addr.network_id || ''}
                                              onChange={(e) => handleSetAddressNetwork(addr.id, e.target.value)}
                                              className="border rounded px-2 py-0.5 text-xs bg-white"
                                              data-testid={`addr-network-select-${addr.id}`}
                                            >
                                              <option value="">— Без сети —</option>
                                              {networks.map(n => (
                                                <option key={n.id} value={n.id}>{n.name}</option>
                                              ))}
                                            </select>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Recent Orders */}
                              <div>
                                <h3 className="font-semibold flex items-center gap-2 mb-3"><ShoppingCart className="w-4 h-4" />Последние заказы</h3>
                                {loadingOrders === net.id ? (
                                  <p className="text-sm text-gray-400 py-4 text-center">Загрузка...</p>
                                ) : (networkOrders[net.id] || []).length === 0 ? (
                                  <p className="text-sm text-gray-400 py-4 text-center">Нет заказов</p>
                                ) : (
                                  <div className="space-y-2">
                                    {(networkOrders[net.id] || []).slice(0, 5).map(o => (
                                      <div key={o.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                        <div>
                                          <p className="text-sm font-medium">#{o.order_number}</p>
                                          <p className="text-xs text-gray-500">{o.manager_name} | {o.delivery_date}</p>
                                        </div>
                                        <div className="text-right">
                                          <p className="text-sm font-semibold">{fmt(o.final_amount)} ₸</p>
                                          <Badge className={`text-xs ${o.status === 'Delivered' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{o.status}</Badge>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Settings & Actions */}
                            <div className="mt-6 pt-4 border-t flex items-center justify-between">
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>Менеджер может: </span>
                                <Badge variant="outline" className={net.settings?.can_edit_legal ? 'border-green-300 text-green-700' : 'border-gray-200'}>
                                  Редакт. юрлицо: {net.settings?.can_edit_legal ? 'Да' : 'Нет'}
                                </Badge>
                                <Badge variant="outline" className={net.settings?.can_add_legal ? 'border-green-300 text-green-700' : 'border-gray-200'}>
                                  Доб. юрлица: {net.settings?.can_add_legal ? 'Да' : 'Нет'}
                                </Badge>
                                <Badge variant="outline" className={net.settings?.can_add_addresses ? 'border-green-300 text-green-700' : 'border-gray-200'}>
                                  Доб. адреса: {net.settings?.can_add_addresses ? 'Да' : 'Нет'}
                                </Badge>
                              </div>
                              <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => openEditNetwork(net)} data-testid={`edit-network-${net.id}`}>
                                  <Edit className="w-3 h-3 mr-1" />Редактировать
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDeleteNetwork(net.id)}>
                                  <Trash2 className="w-3 h-3 mr-1" />Удалить
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </div>

            {/* Analytics Tab */}
            <TabsContent value="analytics">
              {analytics ? (
                <div className="space-y-6">
                  {/* Period Filter */}
                  <Card>
                    <CardContent className="pt-5 pb-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 mr-2">
                          <Filter className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Период:</span>
                        </div>
                        {[
                          { key: 'week', label: 'Неделя' },
                          { key: 'month', label: 'Месяц' },
                          { key: 'quarter', label: 'Квартал' },
                          { key: 'year', label: 'Год' },
                        ].map(p => (
                          <Button
                            key={p.key}
                            size="sm"
                            variant={analyticsPeriod === p.key && !dateFrom ? 'default' : 'outline'}
                            onClick={() => { setAnalyticsPeriod(p.key); setDateFrom(''); setDateTo(''); }}
                            data-testid={`analytics-period-${p.key}`}
                          >
                            {p.label}
                          </Button>
                        ))}
                        <div className="flex items-center gap-2 ml-auto">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <Input
                            type="date"
                            value={dateFrom}
                            onChange={e => setDateFrom(e.target.value)}
                            className="w-36 h-8 text-sm"
                            data-testid="analytics-date-from"
                          />
                          <span className="text-gray-400">—</span>
                          <Input
                            type="date"
                            value={dateTo}
                            onChange={e => setDateTo(e.target.value)}
                            className="w-36 h-8 text-sm"
                            data-testid="analytics-date-to"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Всего заказов</p>
                            <p className="text-3xl font-bold" data-testid="analytics-total-orders">{analytics.totals.orders}</p>
                          </div>
                          <ShoppingCart className="w-8 h-8 text-emerald-500 opacity-60" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Общая сумма</p>
                            <p className="text-3xl font-bold" data-testid="analytics-total-revenue">{fmt(analytics.totals.revenue)} ₸</p>
                          </div>
                          <TrendingUp className="w-8 h-8 text-blue-500 opacity-60" />
                        </div>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-500">Активных сетей</p>
                            <p className="text-3xl font-bold" data-testid="analytics-networks-count">{analytics.totals.networks_count}</p>
                          </div>
                          <Building2 className="w-8 h-8 text-violet-500 opacity-60" />
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Analytics Sub-tabs */}
                  <div className="flex gap-1 p-1 bg-gray-100 rounded-lg w-fit">
                    {[
                      { key: 'products', label: 'Топ товаров', icon: Package },
                      { key: 'categories', label: 'По категориям', icon: Layers },
                      { key: 'networks', label: 'По сетям', icon: Building2 },
                    ].map(t => (
                      <button
                        key={t.key}
                        onClick={() => setAnalyticsTab(t.key)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                          analyticsTab === t.key
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                        data-testid={`analytics-subtab-${t.key}`}
                      >
                        <t.icon className="w-4 h-4" />{t.label}
                      </button>
                    ))}
                  </div>

                  {/* Top Products */}
                  {analyticsTab === 'products' && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Package className="w-5 h-5 text-emerald-600" />
                          Самые заказываемые товары
                        </CardTitle>
                        <CardDescription>Рейтинг по количеству заказов за выбранный период</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {analytics.top_products?.length > 0 ? (
                          <div className="space-y-2">
                            {analytics.top_products.map((p, i) => {
                              const maxQty = analytics.top_products[0]?.quantity || 1;
                              const pct = Math.round((p.quantity / maxQty) * 100);
                              return (
                                <div key={i} className="group" data-testid={`top-product-${i}`}>
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-bold text-gray-400 w-5">{i + 1}</span>
                                      <span className="text-sm font-medium text-gray-800">{p.name}</span>
                                    </div>
                                    <div className="flex items-center gap-4 text-sm">
                                      <span className="font-semibold text-gray-700">{p.quantity} шт.</span>
                                      <span className="text-gray-500">{fmt(p.revenue)} ₸</span>
                                    </div>
                                  </div>
                                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden ml-7">
                                    <div
                                      className="h-full bg-emerald-500 rounded-full transition-all duration-500"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-400">
                            <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
                            <p>Нет данных за выбранный период</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Categories */}
                  {analyticsTab === 'categories' && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Layers className="w-5 h-5 text-blue-600" />
                          Аналитика по категориям
                        </CardTitle>
                        <CardDescription>Какие категории товаров популярнее</CardDescription>
                      </CardHeader>
                      <CardContent>
                        {analytics.categories?.length > 0 ? (
                          <div className="space-y-3">
                            {analytics.categories.map((c, i) => {
                              const maxRev = analytics.categories[0]?.revenue || 1;
                              const pct = Math.round((c.revenue / maxRev) * 100);
                              return (
                                <div key={i} data-testid={`category-${i}`}>
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-sm font-medium text-gray-800">{c.name}</span>
                                    <div className="flex items-center gap-4 text-sm">
                                      <span className="text-gray-500">{c.quantity} шт.</span>
                                      <span className="text-gray-500">{c.orders_count} заказов</span>
                                      <span className="font-semibold text-gray-700">{fmt(c.revenue)} ₸</span>
                                    </div>
                                  </div>
                                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                      className="h-full bg-blue-500 rounded-full transition-all duration-500"
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-400">
                            <Layers className="w-10 h-10 mx-auto mb-2 opacity-40" />
                            <p>Нет данных за выбранный период</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Per-Network */}
                  {analyticsTab === 'networks' && (
                    <div className="space-y-4">
                      {analytics.networks.map(net => (
                        <Card key={net.network_id} data-testid={`network-analytics-${net.network_id}`}>
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-lg flex items-center gap-2">
                                <Building2 className="w-5 h-5 text-violet-600" />{net.network_name}
                              </CardTitle>
                              <div className="flex items-center gap-4 text-sm">
                                <Badge variant="outline">{net.orders_count} заказов</Badge>
                                <span className="font-bold text-gray-900">{fmt(net.total_revenue)} ₸</span>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">Популярные товары</h4>
                                {net.top_products.length === 0 ? (
                                  <p className="text-sm text-gray-400">Нет данных</p>
                                ) : (
                                  <div className="space-y-1.5">
                                    {net.top_products.slice(0, 5).map((p, i) => (
                                      <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-50">
                                        <span className="text-gray-600">{p.name}</span>
                                        <div className="flex gap-3">
                                          <span className="font-medium">{p.quantity} шт.</span>
                                          <span className="text-gray-400">{fmt(p.revenue)} ₸</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div>
                                <h4 className="text-sm font-semibold text-gray-700 mb-3">Заказы по менеджерам</h4>
                                {net.orders_by_manager.length === 0 ? (
                                  <p className="text-sm text-gray-400">Нет данных</p>
                                ) : (
                                  <div className="space-y-1.5">
                                    {net.orders_by_manager.map((m, i) => (
                                      <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-50">
                                        <span className="text-gray-600">{m.name}</span>
                                        <span className="font-medium">{m.count} зак. / {fmt(m.revenue)} ₸</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                      {analytics.networks.length === 0 && (
                        <Card><CardContent className="py-12 text-center text-gray-500">
                          <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>Нет данных для аналитики</p>
                        </CardContent></Card>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Card><CardContent className="py-12 text-center text-gray-500">Загрузка аналитики...</CardContent></Card>
              )}
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings">
              <div className="space-y-6">
                {/* Change Password */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <Lock className="w-5 h-5 text-emerald-700" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Смена пароля</CardTitle>
                        <CardDescription>Обновите пароль для защиты аккаунта</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleChangePassword} className="space-y-4">
                      <div>
                        <Label htmlFor="settings_current_password">Текущий пароль</Label>
                        <div className="relative mt-1">
                          <Input id="settings_current_password" type={showCurrentPassword ? 'text' : 'password'} value={passwordData.current_password} onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })} required data-testid="settings-current-password" />
                          <button type="button" onClick={() => setShowCurrentPassword(!showCurrentPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                            {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="settings_new_password">Новый пароль</Label>
                        <div className="relative mt-1">
                          <Input id="settings_new_password" type={showNewPasswordField ? 'text' : 'password'} value={passwordData.new_password} onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })} required minLength={6} data-testid="settings-new-password" />
                          <button type="button" onClick={() => setShowNewPasswordField(!showNewPasswordField)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                            {showNewPasswordField ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="settings_confirm_password">Подтвердите новый пароль</Label>
                        <Input id="settings_confirm_password" type="password" value={passwordData.confirm_password} onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })} required className="mt-1" data-testid="settings-confirm-password" />
                      </div>
                      <Button type="submit" disabled={changingPassword} className="bg-emerald-700 hover:bg-emerald-800" data-testid="settings-change-password-btn">
                        {changingPassword ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Сохранение...</> : 'Обновить пароль'}
                      </Button>
                    </form>
                  </CardContent>
                </Card>

                {/* Notifications - Network Orders (existing) */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Bell className="w-5 h-5 text-emerald-600" />
                      Уведомления
                    </CardTitle>
                    <CardDescription>Настройте, какие уведомления вы хотите получать</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {isOwner && (
                        <div className="flex items-center justify-between py-2">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Уведомления о заказах от сетей</p>
                            <p className="text-sm text-gray-500">Получать уведомление когда менеджер оформляет заказ</p>
                          </div>
                          <Switch checked={notifyNetworkOrders} onCheckedChange={toggleNotifyNetworkOrders} data-testid="toggle-notify-network-orders" />
                        </div>
                      )}
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Обновления заказов</p>
                          <p className="text-sm text-gray-500">Уведомления о статусе ваших заказов</p>
                        </div>
                        <Switch checked={settingsNotifications.order_updates} onCheckedChange={(checked) => setSettingsNotifications({ ...settingsNotifications, order_updates: checked })} data-testid="toggle-order-updates" />
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Акции и скидки</p>
                          <p className="text-sm text-gray-500">Информация о специальных предложениях</p>
                        </div>
                        <Switch checked={settingsNotifications.promotions} onCheckedChange={(checked) => setSettingsNotifications({ ...settingsNotifications, promotions: checked })} data-testid="toggle-promotions" />
                      </div>
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Новостная рассылка</p>
                          <p className="text-sm text-gray-500">Новости компании и новинки</p>
                        </div>
                        <Switch checked={settingsNotifications.newsletter} onCheckedChange={(checked) => setSettingsNotifications({ ...settingsNotifications, newsletter: checked })} data-testid="toggle-newsletter" />
                      </div>
                      <Button onClick={handleSaveNotifications} disabled={savingNotifications} variant="outline" className="mt-2" data-testid="save-notifications-btn">
                        {savingNotifications ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Сохранение...</> : 'Сохранить настройки'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Login History */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <History className="w-5 h-5 text-blue-700" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">История входов</CardTitle>
                        <CardDescription>Последние входы в ваш аккаунт</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {loadingHistory ? (
                      <div className="flex justify-center py-4"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                    ) : loginHistory.length > 0 ? (
                      <div className="space-y-3">
                        {loginHistory.slice(0, 10).map((entry, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`login-history-${idx}`}>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{entry.device || 'Неизвестное устройство'}</p>
                              <p className="text-xs text-gray-500">{entry.ip_address || '—'}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm text-gray-600">{new Date(entry.timestamp).toLocaleDateString()}</p>
                              <p className="text-xs text-gray-500">{new Date(entry.timestamp).toLocaleTimeString()}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-gray-500 py-4">История входов пуста</p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        ) : (
          /* Manager view - simplified */
          <div className="space-y-6">
            {networks.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5" />{networks[0].name}</CardTitle>
                  <CardDescription>Ваша сеть</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Заказов</p>
                      <p className="text-2xl font-bold">{networks[0].stats?.orders_count || 0}</p>
                    </div>
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500">Сумма заказов</p>
                      <p className="text-2xl font-bold">{fmt(networks[0].stats?.total_revenue || 0)} ₸</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Network Dialog */}
      <Dialog open={showNetworkDialog} onOpenChange={setShowNetworkDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingNetwork ? 'Редактировать сеть' : 'Новая сеть'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Название сети *</Label>
              <Input value={networkForm.name} onChange={e => setNetworkForm(p => ({ ...p, name: e.target.value }))} placeholder="Например: Сеть Алма" data-testid="network-name-input" />
            </div>
            <div>
              <Label>Описание</Label>
              <Input value={networkForm.description} onChange={e => setNetworkForm(p => ({ ...p, description: e.target.value }))} placeholder="Описание сети" />
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-semibold mb-3">Права менеджера:</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Может редактировать юрлицо</Label>
                  <Switch checked={networkForm.settings?.can_edit_legal || false} onCheckedChange={v => setNetworkForm(p => ({ ...p, settings: { ...p.settings, can_edit_legal: v } }))} data-testid="switch-can-edit-legal" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Может добавлять юрлица</Label>
                  <Switch checked={networkForm.settings?.can_add_legal || false} onCheckedChange={v => setNetworkForm(p => ({ ...p, settings: { ...p.settings, can_add_legal: v } }))} data-testid="switch-can-add-legal" />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-sm">Может добавлять адреса</Label>
                  <Switch checked={networkForm.settings?.can_add_addresses || false} onCheckedChange={v => setNetworkForm(p => ({ ...p, settings: { ...p.settings, can_add_addresses: v } }))} data-testid="switch-can-add-addresses" />
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowNetworkDialog(false)}>Отмена</Button>
              <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800" onClick={handleSaveNetwork} data-testid="save-network-btn">
                {editingNetwork ? 'Сохранить' : 'Создать'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Manager Dialog */}
      <Dialog open={showManagerDialog} onOpenChange={setShowManagerDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Новый менеджер</DialogTitle>
            <DialogDescription>Создайте аккаунт для менеджера сети</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>ФИО *</Label>
              <Input value={managerForm.full_name} onChange={e => setManagerForm(p => ({ ...p, full_name: e.target.value }))} placeholder="Иванов Иван" data-testid="manager-name-input" />
            </div>
            <div>
              <Label>Логин *</Label>
              <Input value={managerForm.username} onChange={e => setManagerForm(p => ({ ...p, username: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') }))} placeholder="aidos" data-testid="manager-username-input" />
              <p className="text-xs text-gray-400 mt-1">Латинские буквы, цифры, точка, дефис</p>
            </div>
            <div>
              <Label>Пароль *</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={managerForm.password} onChange={e => setManagerForm(p => ({ ...p, password: e.target.value }))} placeholder="Минимум 6 символов" data-testid="manager-password-input" />
                <button type="button" className="absolute right-3 top-2.5 text-gray-400" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>Email <span className="text-gray-400 text-xs">(необязательно)</span></Label>
              <Input type="email" value={managerForm.email} onChange={e => setManagerForm(p => ({ ...p, email: e.target.value }))} placeholder="manager@example.com" />
            </div>
            <div>
              <Label>Телефон <span className="text-gray-400 text-xs">(необязательно)</span></Label>
              <Input value={managerForm.phone} onChange={e => setManagerForm(p => ({ ...p, phone: e.target.value }))} placeholder="+7 700 000 0000" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowManagerDialog(false)}>Отмена</Button>
              <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800" onClick={handleCreateManager} data-testid="save-manager-btn">Создать менеджера</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Сбросить пароль</DialogTitle>
            <DialogDescription>{selectedManager?.full_name} ({selectedManager?.username})</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Новый пароль</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Минимум 6 символов" data-testid="new-password-input" />
                <button type="button" className="absolute right-3 top-2.5 text-gray-400" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowPasswordDialog(false)}>Отмена</Button>
              <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800" onClick={handleResetPassword} data-testid="reset-password-btn">Сбросить</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Manager Dialog */}
      <Dialog open={showEditManagerDialog} onOpenChange={setShowEditManagerDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Редактировать менеджера</DialogTitle>
            <DialogDescription>{selectedManager?.username}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div><Label>ФИО</Label><Input value={editManagerForm.full_name} onChange={e => setEditManagerForm(p => ({ ...p, full_name: e.target.value }))} /></div>
            <div><Label>Телефон</Label><Input value={editManagerForm.phone} onChange={e => setEditManagerForm(p => ({ ...p, phone: e.target.value }))} /></div>
            <div><Label>Email</Label><Input type="email" value={editManagerForm.email} onChange={e => setEditManagerForm(p => ({ ...p, email: e.target.value }))} /></div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowEditManagerDialog(false)}>Отмена</Button>
              <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800" onClick={handleEditManager}>Сохранить</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Legal Entity Dialog */}
      <Dialog open={showLegalDialog} onOpenChange={setShowLegalDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingLegalId ? 'Редактировать юрлицо' : 'Добавить юрлицо'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div><Label>Название компании *</Label><Input value={legalForm.company_name} onChange={e => setLegalForm(p => ({ ...p, company_name: e.target.value }))} placeholder='ТОО "Алма"' data-testid="legal-name-input" /></div>
            <div><Label>БИН/ИИН</Label><Input value={legalForm.bin_iin} onChange={e => setLegalForm(p => ({ ...p, bin_iin: e.target.value }))} placeholder="123456789012" /></div>
            <div><Label>Юридический адрес</Label><Input value={legalForm.legal_address} onChange={e => setLegalForm(p => ({ ...p, legal_address: e.target.value }))} /></div>
            <div><Label>Контактное лицо</Label><Input value={legalForm.contact_person} onChange={e => setLegalForm(p => ({ ...p, contact_person: e.target.value }))} /></div>
            <div><Label>Телефон</Label><Input value={legalForm.phone} onChange={e => setLegalForm(p => ({ ...p, phone: e.target.value }))} /></div>
            <div><Label>Банк</Label><Input value={legalForm.bank_name} onChange={e => setLegalForm(p => ({ ...p, bank_name: e.target.value }))} /></div>
            <div><Label>Расчётный счёт</Label><Input value={legalForm.bank_account} onChange={e => setLegalForm(p => ({ ...p, bank_account: e.target.value }))} /></div>
            <div className="flex items-center gap-2">
              <Switch checked={legalForm.is_default} onCheckedChange={v => setLegalForm(p => ({ ...p, is_default: v }))} />
              <Label className="text-sm">Юрлицо по умолчанию</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowLegalDialog(false)}>Отмена</Button>
              <Button className="flex-1 bg-emerald-700 hover:bg-emerald-800" onClick={handleSaveLegal} data-testid="save-legal-btn">{editingLegalId ? 'Сохранить' : 'Добавить'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Address Dialog */}
      <Dialog open={showAddAddressDialog} onOpenChange={setShowAddAddressDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle>Добавить адрес для сети</DialogTitle></DialogHeader>
          <form onSubmit={handleAddNetworkAddress} className="space-y-4">
            <div>
              <Label>Название</Label>
              <Input placeholder="Офис, Склад, Точка..." value={addressForm.label} onChange={e => setAddressForm({...addressForm, label: e.target.value})} data-testid="address-label" />
            </div>
            <div>
              <Label>Адрес</Label>
              <AddressAutocomplete
                value={addressForm.address_line}
                onChange={val => setAddressForm({...addressForm, address_line: val})}
                onAddressSelect={data => {
                  setAddressForm(prev => ({
                    ...prev,
                    address_line: data.address || prev.address_line,
                    city: data.city || prev.city
                  }));
                }}
                placeholder="Начните вводить адрес..."
                cityCode="almaty"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Город</Label>
                <Input value={addressForm.city} onChange={e => setAddressForm({...addressForm, city: e.target.value})} data-testid="address-city" />
              </div>
              <div>
                <Label>Телефон</Label>
                <Input placeholder="+7..." value={addressForm.phone} onChange={e => setAddressForm({...addressForm, phone: e.target.value})} data-testid="address-phone" />
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
              <Switch checked={addressForm.is_delivery} onCheckedChange={v => setAddressForm({...addressForm, is_delivery: v})} data-testid="address-is-delivery" />
              <div>
                <p className="text-sm font-medium text-emerald-800">Адрес доставки</p>
                <p className="text-xs text-emerald-600">Этот адрес будет автоматически выбран при оформлении заказа менеджером</p>
              </div>
            </div>
            <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="save-address-btn">Сохранить адрес</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default B2BNetworks;
